// src/pages/common/Conferencia.tsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableList, faListCheck, faTruckRampBox, faList } from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import KanbanColumn from '../../components/common/KanbanColumn';
import KanbanCard from '../../components/common/KanbanCard';
import SidePanel from '../../components/common/SidePanel';
import IconBoxOpen from '../../components/common/IconBoxOpen';
import RecebimentoPendenteItemsSidePanelContent from './RecebimentoPendenteItemsSidePanelContent';
import ConferenciaRascunhoItemsSidePanelContent from './ConferenciaRascunhoItemsSidePanelContent';
import NewReceiptItemsSidePanelContent from './NewReceiptItemsSidePanelContent';
import { formatDate, formatDateTime, isDateOverdue } from '../../utils/formatUtils';
import './Conferencia.css';
import ReceiptCompletedItemsSidePanelContent from './ReceiptCompletedItemsSidePanelContent';
import IconSearchButton from '../../components/common/IconSearchButton';
import IconResetButton from '../../components/common/IconResetButton';
import Modal from '../../components/common/Modal';
import AdvancedSearchConferenciaKanbanModalContent from './AdvancedSearchConferenciaKanbanModalContent';
import {
  useConferenciaKanban,
  RecebimentoPendenteItem,
  ConferenciaRascunhoItem,
  ConferenciaFinalizadoItem,
} from '../../hooks/useConferenciaKanban';
import { useConferenciaMutations } from '../../hooks/useConferenciaMutations';
import IconExportButton from '../../components/common/IconExportButton';
import ConferenciaExportExcelModalContent from './ConferenciaExportExcelModalContent';

export type { ConferenciaRascunhoItem, ConferenciaFinalizadoItem };

const Conferencia = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();

  const [showItemsPanel, setShowItemsPanel] = useState(false);
  const [selectedRecebimentoPendente, setSelectedRecebimentoPendente] = useState<RecebimentoPendenteItem | null>(null);
  const [selectedConferenciaRascunho, setSelectedConferenciaRascunho] = useState<Partial<ConferenciaRascunhoItem> | null>(null);
  const [selectedRecebimentoFinalizado, setSelectedRecebimentoFinalizado] = useState<ConferenciaFinalizadoItem | null>(
    null
  );

  const [showNewReceiptPanel, setShowNewReceiptPanel] = useState(false);

  const pendentesSentinelRef = useRef<HTMLDivElement>(null);
  const rascunhoSentinelRef = useRef<HTMLDivElement>(null);
  const finalizadosSentinelRef = useRef<HTMLDivElement>(null);
  const shouldRefreshOnCloseRef = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const kanbans = useConferenciaKanban();
  const mutations = useConferenciaMutations({
    pendentes: kanbans.pendentes,
    rascunho: kanbans.rascunho,
    finalizados: kanbans.finalizados,
  });

  const handleOpenSearchModal = () => setIsModalOpen(true);

  useEffect(() => {
    document.title = 'Bluelt | Conferência';
  }, []);

  useEffect(() => {
    if (kanbans.pendentes.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.pendentes.isLoading) kanbans.pendentes.loadMore();
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = pendentesSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.pendentes.items, kanbans.pendentes.isLoading, kanbans.pendentes.loadMore]);

  useEffect(() => {
    if (kanbans.rascunho.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.rascunho.isLoading) kanbans.rascunho.loadMore();
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = rascunhoSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.rascunho.items, kanbans.rascunho.isLoading, kanbans.rascunho.loadMore]);

  useEffect(() => {
    if (kanbans.finalizados.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.finalizados.isLoading) kanbans.finalizados.loadMore();
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = finalizadosSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.finalizados.items, kanbans.finalizados.isLoading, kanbans.finalizados.loadMore]);

  const handleViewRecebimentoPendenteDetails = useCallback((item: RecebimentoPendenteItem) => {
    setSelectedRecebimentoPendente(item);
    setShowItemsPanel(true);
  }, []);

  const handleViewConferenciaRascunhoDetails = useCallback((item: ConferenciaRascunhoItem) => {
    setSelectedConferenciaRascunho(item);
    setShowItemsPanel(true);
  }, []);

  const handleViewRecebimentoFinalizadoDetails = useCallback((item: ConferenciaFinalizadoItem) => {
    setSelectedRecebimentoFinalizado(item);
    setShowItemsPanel(true);
  }, []);

  const markRefreshOnClose = () => {
    shouldRefreshOnCloseRef.current = true;
  };

  const handleSuccessCreateConferencia = (newId: number) => {
    setSelectedRecebimentoPendente(null);
    setSelectedConferenciaRascunho({ id: newId });
    kanbans.pendentes.reset();
    kanbans.rascunho.reset();
  };

  const handleClosePanel = () => {
    setShowItemsPanel(false);
    setSelectedRecebimentoPendente(null);
    setSelectedConferenciaRascunho(null);
    setSelectedRecebimentoFinalizado(null);
    setShowNewReceiptPanel(false);

    if (shouldRefreshOnCloseRef.current) {
      shouldRefreshOnCloseRef.current = false;
      kanbans.handleResetSearch();
    }
    // reloadAllColumns garante refetch mesmo quando handleResetSearch foi no-op
    // (search já estava off → setState não muda nada → cascata fetchFn deps não dispara).
    kanbans.reloadAllColumns();
  };


  const renderRecebimentoPendenteItem = useCallback((item: RecebimentoPendenteItem) => (
    <KanbanCard<RecebimentoPendenteItem>
      item={item}
      renderContent={(c) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Ordem de Compra {c.id}</strong>
          {c.origem !== 'PLANEJAMENTO' && <strong>Possui encomenda!</strong>}
          <div>Fornecedor: {c.nomeFornecedor}</div>
          <div>Comprador: {c.nomeUsuario}</div>
          <div>
            {c.countDiasAberto === 0
              ? 'Aberto hoje'
              : c.countDiasAberto < 0
              ? 'Dias em Aberto: Erro'
              : `Aberto há ${c.countDiasAberto} dia${c.countDiasAberto === 1 ? '' : 's'}`}
          </div>
          <div>Quantidade Itens: {c.countItens}</div>
          <div>
            Previsão de chegada: {formatDate(c.dataEntrega)}
            {isDateOverdue(c.dataEntrega) && <strong> - EM ATRASO!</strong>}
          </div>
          <div>Loja de entrega: {c.nomeLojaEntrega}</div>
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartPendente}
      actionButton={{
        icon: faTableList,
        onClick: () => handleViewRecebimentoPendenteDetails(item),
        title: 'Visualizar detalhes',
      }}
      className="recebimento-pendente-card"
      isValid={!isDateOverdue(item.dataEntrega)}
    />
  ), [mutations.onDragStartPendente, handleViewRecebimentoPendenteDetails]);

  const renderConferenciaRascunhoItem = useCallback((item: ConferenciaRascunhoItem) => (
    <KanbanCard<ConferenciaRascunhoItem>
      item={item}
      renderContent={(r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Conferência nº: {r.id}</strong>
          <div>Fornecedor: {r.nomeFornecedor}</div>
          <div>Nº Nota Fiscal: {r.nroNotaFiscal ? r.nroNotaFiscal : 'Não informado'}</div>
          <div>Conferente: {r.nomeUsuario}</div>
          <div>
            {r.countDiasAberto === 0
              ? 'Conferência aberta hoje'
              : r.countDiasAberto < 0
              ? 'Dias em Aberto: Erro'
              : `Em conferência há ${r.countDiasAberto} dia${r.countDiasAberto === 1 ? '' : 's'}`}
          </div>
          <div>Quantidade Itens: {r.countItens}</div>
          <div>Recebendo na loja: {r.nomeLojaEntrega}</div>
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartRascunho}
      actionButton={{
        icon: faTableList,
        onClick: () => handleViewConferenciaRascunhoDetails(item),
        title: 'Visualizar detalhes',
      }}
      className="conferencia-rascunho-card"
    />
  ), [mutations.onDragStartRascunho, handleViewConferenciaRascunhoDetails]);

  const renderRecebimentoFinalizadoItem = useCallback((item: ConferenciaFinalizadoItem) => (
    <KanbanCard<ConferenciaFinalizadoItem>
      item={item}
      renderContent={(r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Recebimento nº: {r.id}</strong>
          <div>Fornecedor: {r.nomeFornecedor}</div>
          <div>Nº Nota Fiscal: {r.nroNotaFiscal ? r.nroNotaFiscal : 'Não informado'}</div>
          <div>Conferente: {r.nomeUsuario}</div>
          <div>Data de encerramento: {formatDateTime(r.dataEncerrada)}</div>
          <div>Quantidade Itens: {r.countItens}</div>
          <div>Loja de recebimento: {r.nomeLojaEntrega}</div>
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartFinalizado}
      actionButton={{
        icon: faTableList,
        onClick: () => handleViewRecebimentoFinalizadoDetails(item),
        title: 'Visualizar Detalhes',
      }}
      className="recebimento-finalizado-card"
    />
  ), [mutations.onDragStartFinalizado, handleViewRecebimentoFinalizadoDetails]);

  const requiredPermission = 'modulo.gestao_conferencias';
  if (!hasPermission(requiredPermission)) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  return (
    <div className={`${theme}`}>
      <main className="conferencia-main">
        <h1>
          <FontAwesomeIcon icon={faListCheck} /> Gestão de Recebimento e Conferência
        </h1>

        <div className="header-actions">
          <IconBoxOpen onClick={() => setShowNewReceiptPanel(true)} title="Novo recebimento" />
          <IconExportButton onClick={() => setIsExportModalOpen(true)} title="Exportar para Excel" />
          {kanbans.isSearchActive && <IconResetButton onClick={kanbans.handleResetSearch} title="Limpar Pesquisa" />}
          <IconSearchButton onClick={handleOpenSearchModal} title="Pesquisar" />
        </div>
      </main>

      <div className="conferencia-content">
        <div className="kanban-container">
          <KanbanColumn<RecebimentoPendenteItem>
            title="Aguardando Recebimento"
            icon={faTruckRampBox}
            data={kanbans.pendentes.items}
            renderItem={renderRecebimentoPendenteItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'pendente')}
            ref={pendentesSentinelRef}
            emptyMessage="Nenhum recebimento pendente encontrado"
            showCountCircle={true}
            totalCount={kanbans.pendentes.totalItems}
          />

          <KanbanColumn<ConferenciaRascunhoItem>
            title="Em Recebimento e Conferência"
            icon={faList}
            data={kanbans.rascunho.items}
            renderItem={renderConferenciaRascunhoItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'rascunho')}
            ref={rascunhoSentinelRef}
            emptyMessage="Nenhuma conferência em rascunho encontrada"
            showCountCircle={true}
            totalCount={kanbans.rascunho.totalItems}
          />

          <KanbanColumn<ConferenciaFinalizadoItem>
            title="Recebimentos Finalizados"
            icon={faListCheck}
            data={kanbans.finalizados.items}
            renderItem={renderRecebimentoFinalizadoItem}
            onDragOver={mutations.onDragOver}
            onDrop={(e) => mutations.onDrop(e, 'finalizado')}
            ref={finalizadosSentinelRef}
            emptyMessage="Nenhum recebimento finalizado encontrado"
            showCountCircle={true}
            totalCount={kanbans.finalizados.totalItems}
          />
        </div>
      </div>

      <SidePanel isOpen={showItemsPanel || showNewReceiptPanel} onClose={handleClosePanel} theme={theme}>
        {selectedRecebimentoPendente && (
          <RecebimentoPendenteItemsSidePanelContent
            recebimentoPendente={selectedRecebimentoPendente}
            theme={theme}
            onClose={handleClosePanel}
            onSuccessCreateConferencia={handleSuccessCreateConferencia}
          />
        )}

        {selectedConferenciaRascunho && (
          <ConferenciaRascunhoItemsSidePanelContent
            conferenciaRascunho={selectedConferenciaRascunho as ConferenciaRascunhoItem}
            theme={theme}
            onClose={handleClosePanel}
            onPersistSuccess={markRefreshOnClose}
          />
        )}

        {selectedRecebimentoFinalizado && (
          <ReceiptCompletedItemsSidePanelContent
            recebimentoFinalizado={selectedRecebimentoFinalizado}
            theme={theme}
            onClose={handleClosePanel}
          />
        )}

        {showNewReceiptPanel && (
          <NewReceiptItemsSidePanelContent
            theme={theme}
            onClose={handleClosePanel}
            onPersistSuccess={markRefreshOnClose}
          />
        )}
      </SidePanel>

      <Modal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} theme={theme}>
        <ConferenciaExportExcelModalContent
          theme={theme}
          onClose={() => setIsExportModalOpen(false)}
          allIdsRascunho={kanbans.allIdsRascunho}
          allIdsFinalizado={kanbans.allIdsFinalizado}
        />
      </Modal>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} theme={theme}>
        <AdvancedSearchConferenciaKanbanModalContent
          theme={theme}
          onClose={() => setIsModalOpen(false)}
          onSearch={(result) => {
            // setSearchQuery dispara refetch automático nas 3 colunas (cascata via deps
            // do fetchFunction). allIds são populados pelo fetchRascunho/fetchFinalizados
            // quando pageNumber === 0, sem necessidade de armazenar aqui.
            kanbans.handleSearch(result.__query || '');
          }}
        />
      </Modal>
    </div>
  );
};

export default Conferencia;
